import { motion } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";
import { PRICING } from "../../data/site";

export default function Pricing() {
  return (
    <section
      id="pricing"
      className="py-20 md:py-28 bg-[#FAFAFA]"
      data-testid="pricing-section"
    >
      <div className="container">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <p className="text-overline text-electric mb-3">08 — Pricing</p>
          <h2 className="font-display text-4xl md:text-5xl font-bold text-charcoal text-balance">
            Transparent pricing. No lock-ins. Cancel anytime.
          </h2>
          <p className="mt-5 text-lg text-zinc-500">
            Pick a plan to get started, or talk to us about a custom enterprise
            engagement.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 items-start">
          {PRICING.map((p, idx) => (
            <motion.article
              key={p.tier}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: idx * 0.08 }}
              className={`relative p-8 md:p-10 flex flex-col rounded-2xl border transition-all ${
                p.highlighted
                  ? "bg-charcoal text-white border-charcoal shadow-glow-brand -translate-y-2"
                  : "bg-white border-zinc-100 shadow-card hover:shadow-card-hover hover:-translate-y-1"
              }`}
              data-testid={`pricing-tier-${p.tier.toLowerCase()}`}
            >
              {p.highlighted && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-electric text-white text-[10px] font-bold uppercase tracking-widest px-4 py-1.5 rounded-full whitespace-nowrap">
                  Most Popular
                </span>
              )}
              <div>
                <p
                  className={`text-overline mb-3 ${
                    p.highlighted ? "text-electric-400" : "text-zinc-400"
                  }`}
                >
                  {p.tier}
                </p>
                <div className="flex items-baseline gap-1">
                  <span className="font-display text-5xl md:text-6xl font-bold tracking-tight">
                    {p.price}
                  </span>
                  {p.cadence && (
                    <span className={`text-sm ${p.highlighted ? "text-zinc-400" : "text-zinc-400"}`}>
                      {p.cadence}
                    </span>
                  )}
                </div>
                <p className={`mt-4 text-sm leading-relaxed ${p.highlighted ? "text-zinc-300" : "text-zinc-500"}`}>
                  {p.description}
                </p>
              </div>

              <ul className="mt-8 space-y-3 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm">
                    <Check
                      className="w-4 h-4 mt-0.5 flex-shrink-0 text-electric"
                      strokeWidth={3}
                    />
                    <span className={p.highlighted ? "text-zinc-200" : "text-zinc-600"}>
                      {f}
                    </span>
                  </li>
                ))}
              </ul>

              <a
                href="#audit"
                data-testid={`pricing-cta-${p.tier.toLowerCase()}`}
                aria-label={`${p.cta} — Lumotis ${p.tier} plan`}
                className={`mt-10 inline-flex items-center justify-center gap-2 px-5 py-3.5 font-semibold rounded-xl transition-all ${
                  p.highlighted
                    ? "bg-electric text-white hover:bg-electric-500 shadow-lg shadow-electric/30"
                    : "bg-charcoal text-white hover:bg-electric hover:shadow-lg hover:shadow-electric/30"
                }`}
              >
                {p.cta} <ArrowRight className="w-4 h-4" />
              </a>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
